import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Card,
  Statistic,
  Divider,
  Modal,
  Image,
  Segment,
  Radio,
  Message,
  Icon,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import { useTranslation } from 'react-i18next';

const TopUp = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [redemptionCode, setRedemptionCode] = useState('');
  const [topUpLink, setTopUpLink] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]); // 充值历史记录
  
  // 充值弹窗状态
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState(''); // 自定义金额输入
  const [paymentMethod, setPaymentMethod] = useState('alipay'); // 'alipay' or 'wechat'
  const [qrCode, setQrCode] = useState('');
  const [orderId, setOrderId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, paid, failed
  const [countdown, setCountdown] = useState(1800); // 30分钟倒计时
  const timerRef = useRef(null);
  const pollTimerRef = useRef(null);
  
  // 充值金额选项
  const amountOptions = [
    { value: 1, label: '¥1', quota: '1 quota' },
    { value: 100, label: '¥100', quota: '100 quota' },
    { value: 200, label: '¥200', quota: '200 quota' },
    { value: 300, label: '¥300', quota: '300 quota' },
    { value: 400, label: '¥400', quota: '400 quota' },
    { value: 500, label: '¥500', quota: '500 quota' },
  ];

  const topUp = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => {
          return quota + data;
        });
        setRedemptionCode('');
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTopUpLink = () => {
    // 英文环境跳转到联系我们页面
    if (currentLanguage === 'en') {
      window.location.href = '/contact-us';
    } else {
      // 中文环境打开支付弹窗
      setPaymentModalOpen(true);
    }
  };

  const handlePayment = async () => {
    // 校验金额
    const amount = Number(selectedAmount);
    if (!amount || isNaN(amount) || amount < 1 || amount > 10000) {
      showError('充值金额必须在 1-10000 元之间');
      return;
    }
    try {
      const res = await API.post('/api/payment/create', {
        amount: Math.round(amount * 100) / 100,
        payment_method: paymentMethod,
      });

      if (res.data.success) {
        const { order_id, qr_code } = res.data.data;
        setOrderId(order_id);
        setQrCode(qr_code);
        setPaymentStatus('pending');
        setCountdown(1800);

        // 开始倒计时
        timerRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // 开始轮询支付状态
        pollTimerRef.current = setInterval(async () => {
          try {
            const statusRes = await API.get(`/api/payment/status/${order_id}`);
            if (statusRes.data.success) {
              const { status } = statusRes.data.data;
              if (status === 1) { // 已支付
                clearInterval(timerRef.current);
                clearInterval(pollTimerRef.current);
                setPaymentStatus('paid');
                showSuccess('充值成功！');
                getUserQuota(); // 刷新余额
                setTimeout(() => {
                  setPaymentModalOpen(false);
                  resetPaymentState();
                }, 2000);
              }
            }
          } catch (err) {
            console.error('查询支付状态失败:', err);
          }
        }, 3000); // 每3秒查询一次
      } else {
        showError(res.data.message || '创建支付订单失败');
      }
    } catch (err) {
      showError('创建支付订单失败: ' + err.message);
    }
  };

  const resetPaymentState = () => {
    setQrCode('');
    setOrderId('');
    setPaymentStatus('pending');
    setCountdown(1800);
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  };

  // 获取充值历史
  const getPaymentHistory = async () => {
    try {
      const res = await API.get('/api/payment/history');
      if (res.data && res.data.success) {
        setPaymentHistory(res.data.data || []);
      } else {
        console.error('获取充值历史失败:', res.data);
      }
    } catch (err) {
      console.error('获取充值历史失败:', err);
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
      }
    }
  };

  const getUserQuota = async () => {
    let res = await API.get(`/api/user/self`);
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
      setUser(data);
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      if (status.top_up_link) {
        setTopUpLink(status.top_up_link);
      }
    }
    getUserQuota().then();
    getPaymentHistory().then();
  }, []);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>
            <Header as='h2'>{t('topup.title')}</Header>
          </Card.Header>

          <Grid columns={2} stackable>
            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#2185d0', margin: '1em' }}>
                      <i className='credit card icon'></i>
                      {currentLanguage === 'en' ? 'User Quota' : '用户额度'}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ textAlign: 'center', paddingTop: '1em' }}>
                        <Statistic>
                          <Statistic.Value style={{ color: '#2185d0' }}>
                            {renderQuota(userQuota, t)}
                          </Statistic.Value>
                          <Statistic.Label>
                            {t('topup.get_code.current_quota')}
                          </Statistic.Label>
                        </Statistic>
                      </div>

                      <div
                        style={{ 
                          textAlign: 'center', 
                          paddingBottom: '1em',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          primary
                          size='large'
                          onClick={openTopUpLink}
                          style={{ 
                            width: '80%',
                            maxWidth: '400px',
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
                            color: '#333',
                            border: '2px solid #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)';
                            e.currentTarget.style.borderColor = '#b3b3b3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)';
                            e.currentTarget.style.borderColor = '#d9d9d9';
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#07c160">
                            <path d="M8.5 11a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8.5 2C4.36 2 1 4.92 1 8.5c0 2.03 1.07 3.82 2.76 5l-.7 2.1 2.46-1.24c.84.26 1.74.4 2.68.4.22 0 .43-.01.64-.03A6.47 6.47 0 0 1 8.5 13c0-3.58 3.34-6.5 7.5-6.5.34 0 .67.03 1 .07C16.26 3.96 12.64 2 8.5 2zm7.5 7c-4.14 0-7.5 2.92-7.5 6.5s3.36 6.5 7.5 6.5c.82 0 1.62-.12 2.36-.34L20.5 23l-.58-1.74C21.32 19.88 22.5 18.12 22.5 16c0-3.58-3.36-7-6.5-7zm-2.5 9a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/>
                          </svg>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#1677ff">
                            <path d="M21.422 14.752c-1.386-.652-3.199-1.386-5.288-2.082.366-.814.702-1.732.992-2.722h-3.446v-1.46h4.156V7.54h-4.156V5.868h-2.632v1.672h-4.156v.948h4.156v1.46H7.656v.948h6.424c-.26.744-.574 1.46-.926 2.132-1.926-.548-3.996-.652-5.762.026-2.422.932-3.676 3.016-3.362 5.136.314 2.12 2.158 3.494 4.58 3.494 1.34 0 2.612-.368 3.716-1.012 1.484-.864 2.644-2.232 3.33-3.864 1.532.564 2.906 1.208 4.066 1.864.784.444 1.346.924 1.704 1.416.422.582.626 1.218.626 1.88 0 1.476-1.098 2.676-2.454 2.676-1.036 0-1.962-.686-2.468-1.734-.34-.706-.512-1.512-.512-2.366h-2.632c0 1.434.362 2.782 1.014 3.946.914 1.63 2.486 2.786 4.336 2.786 2.998 0 5.428-2.43 5.428-5.428 0-1.582-.682-3.044-1.826-4.076z"/>
                          </svg>
                          <span style={{ fontWeight: '600', fontSize: '15px' }}>
                            {currentLanguage === 'en' ? 'Contact Customer Service for Redemption Code' : '微信/支付宝充值'}
                          </span>
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#21ba45', margin: '1em' }}>
                      <i className='ticket alternate icon'></i>
                      {t('topup.redeem_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Form.Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder={t('topup.redeem_code.placeholder')}
                        value={redemptionCode}
                        onChange={(e) => {
                          setRedemptionCode(e.target.value);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          setRedemptionCode(pastedText.trim());
                        }}
                        action={
                          <Button
                            icon='paste'
                            content={t('topup.redeem_code.paste')}
                            onClick={async () => {
                              try {
                                const text =
                                  await navigator.clipboard.readText();
                                setRedemptionCode(text.trim());
                              } catch (err) {
                                showError(t('topup.redeem_code.paste_error'));
                              }
                            }}
                          />
                        }
                      />

                      <div style={{ paddingBottom: '1em' }}>
                        <Button
                          color='green'
                          fluid
                          size='large'
                          onClick={topUp}
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? t('topup.redeem_code.submitting')
                            : t('topup.redeem_code.submit')}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>

      {/* 充值历史列表 */}
      {paymentHistory.length > 0 && (
        <Card fluid className='chart-card' style={{ marginTop: '20px' }}>
          <Card.Content>
            <Card.Header>
              <Header as='h3'>充值记录</Header>
            </Card.Header>
            <Card.Content>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>订单号</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>金额</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>支付方式</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((order) => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', fontSize: '12px' }}>{order.order_id}</td>
                        <td style={{ padding: '10px', color: '#2185d0', fontWeight: 'bold' }}>¥{order.amount}</td>
                        <td style={{ padding: '10px' }}>
                          {order.payment_method === 'wechat' ? '微信支付' : '支付宝'}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {new Date(order.created_at).toLocaleString('zh-CN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Content>
          </Card.Content>
        </Card>
      )}

      {/* 充值弹窗 */}
      <Modal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        size='small'
        className='payment-modal'
      >
        <Modal.Header className='payment-modal-header'>
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <Icon name='credit card' style={{ fontSize: '32px', color: '#667eea', marginBottom: '10px' }} />
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>充值中心</div>
            <div style={{ fontSize: '14px', color: '#999', marginTop: '5px' }}>选择充值金额和支付方式</div>
          </div>
        </Modal.Header>
        <Modal.Content className='payment-modal-content'>
          {/* 金额选择 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
              <Icon name='money bill alternate' style={{ color: '#667eea', marginRight: '8px' }} />
              充值金额
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {amountOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    setSelectedAmount(option.value);
                    setCustomAmount('');
                  }}
                  style={{
                    cursor: 'pointer',
                    padding: '16px 12px',
                    borderRadius: '12px',
                    border: selectedAmount === option.value ? '2px solid #667eea' : '2px solid #e8e8e8',
                    background: selectedAmount === option.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                    color: selectedAmount === option.value ? '#fff' : '#333',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: selectedAmount === option.value ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedAmount !== option.value) {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAmount !== option.value) {
                      e.currentTarget.style.borderColor = '#e8e8e8';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>
                    {option.label}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {option.quota}
                  </div>
                </div>
              ))}
            </div>
            {/* 自定义金额输入 */}
            <div style={{ marginTop: '12px' }}>
              <Form>
                <Form.Input
                  fluid
                  type='number'
                  min='1'
                  max='10000'
                  step='0.01'
                  placeholder='或输入自定义金额（1-10000 元）'
                  icon='pencil'
                  iconPosition='left'
                  value={customAmount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomAmount(v);
                    if (v !== '' && !isNaN(Number(v))) {
                      setSelectedAmount(Number(v));
                    }
                  }}
                />
              </Form>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                当前充值金额：<span style={{ color: '#667eea', fontWeight: 'bold' }}>¥{Number(selectedAmount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* 支付方式选择 */}
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
              <Icon name='payment' style={{ color: '#667eea', marginRight: '8px' }} />
              支付方式
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <div
                onClick={() => setPaymentMethod('alipay')}
                style={{
                  cursor: 'pointer',
                  padding: '20px',
                  borderRadius: '12px',
                  border: paymentMethod === 'alipay' ? '2px solid #1677ff' : '2px solid #e8e8e8',
                  background: paymentMethod === 'alipay' ? 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)' : '#fff',
                  color: paymentMethod === 'alipay' ? '#fff' : '#333',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: paymentMethod === 'alipay' ? '0 4px 12px rgba(22, 119, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={(e) => {
                  if (paymentMethod !== 'alipay') {
                    e.currentTarget.style.borderColor = '#1677ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (paymentMethod !== 'alipay') {
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill={paymentMethod === 'alipay' ? '#fff' : '#1677ff'} style={{ marginBottom: '8px' }}>
                  <path d="M21.422 14.752c-1.386-.652-3.199-1.386-5.288-2.082.366-.814.702-1.732.992-2.722h-3.446v-1.46h4.156V7.54h-4.156V5.868h-2.632v1.672h-4.156v.948h4.156v1.46H7.656v.948h6.424c-.26.744-.574 1.46-.926 2.132-1.926-.548-3.996-.652-5.762.026-2.422.932-3.676 3.016-3.362 5.136.314 2.12 2.158 3.494 4.58 3.494 1.34 0 2.612-.368 3.716-1.012 1.484-.864 2.644-2.232 3.33-3.864 1.532.564 2.906 1.208 4.066 1.864.784.444 1.346.924 1.704 1.416.422.582.626 1.218.626 1.88 0 1.476-1.098 2.676-2.454 2.676-1.036 0-1.962-.686-2.468-1.734-.34-.706-.512-1.512-.512-2.366h-2.632c0 1.434.362 2.782 1.014 3.946.914 1.63 2.486 2.786 4.336 2.786 2.998 0 5.428-2.43 5.428-5.428 0-1.582-.682-3.044-1.826-4.076z"/>
                </svg>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>支付宝</div>
              </div>
              <div
                onClick={() => setPaymentMethod('wechat')}
                style={{
                  cursor: 'pointer',
                  padding: '20px',
                  borderRadius: '12px',
                  border: paymentMethod === 'wechat' ? '2px solid #07c160' : '2px solid #e8e8e8',
                  background: paymentMethod === 'wechat' ? 'linear-gradient(135deg, #07c160 0%, #06ad56 100%)' : '#fff',
                  color: paymentMethod === 'wechat' ? '#fff' : '#333',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: paymentMethod === 'wechat' ? '0 4px 12px rgba(7, 193, 96, 0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={(e) => {
                  if (paymentMethod !== 'wechat') {
                    e.currentTarget.style.borderColor = '#07c160';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (paymentMethod !== 'wechat') {
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill={paymentMethod === 'wechat' ? '#fff' : '#07c160'} style={{ marginBottom: '8px' }}>
                  <path d="M8.5 11a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8.5 2C4.36 2 1 4.92 1 8.5c0 2.03 1.07 3.82 2.76 5l-.7 2.1 2.46-1.24c.84.26 1.74.4 2.68.4.22 0 .43-.01.64-.03A6.47 6.47 0 0 1 8.5 13c0-3.58 3.34-6.5 7.5-6.5.34 0 .67.03 1 .07C16.26 3.96 12.64 2 8.5 2zm7.5 7c-4.14 0-7.5 2.92-7.5 6.5s3.36 6.5 7.5 6.5c.82 0 1.62-.12 2.36-.34L20.5 23l-.58-1.74C21.32 19.88 22.5 18.12 22.5 16c0-3.58-3.36-7-6.5-7zm-2.5 9a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/>
                </svg>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>微信支付</div>
              </div>
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions className='payment-modal-actions'>
          <Button onClick={() => setPaymentModalOpen(false)} style={{ borderRadius: '8px' }}>
            取消
          </Button>
          <Button
            primary
            onClick={handlePayment}
            style={{
              background: paymentMethod === 'alipay' ? 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)' : 'linear-gradient(135deg, #07c160 0%, #06ad56 100%)',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
            }}
          >
            <Icon name='credit card' />
            充值 {selectedAmount} 元
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 二维码显示弹窗 */}
      <Modal
        open={qrCode !== ''}
        onClose={() => {
          resetPaymentState();
        }}
        size='small'
        className='qr-modal'
      >
        <Modal.Header className='qr-modal-header' style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
            <Icon name='qrcode' style={{ marginRight: '8px', color: '#667eea' }} />
            {paymentMethod === 'wechat' ? '微信扫码支付' : '支付宝扫码支付'}
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: '24px' }}>
          {/* 支付方式标识 */}
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            background: paymentMethod === 'wechat' ? 'linear-gradient(135deg, #07c160 0%, #06ad56 100%)' : 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
            color: '#fff',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <Icon name={paymentMethod === 'wechat' ? 'wechat' : 'payment'} style={{ marginRight: '8px' }} />
            {paymentMethod === 'wechat' ? '请用微信扫描二维码' : '请用支付宝扫描二维码'}
          </div>
          
          {/* 支付金额 */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '12px',
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>支付金额</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ff6b6b', marginBottom: '4px' }}>
              ¥{selectedAmount}
            </div>
            <div style={{ fontSize: '13px', color: '#999' }}>获得 {selectedAmount} quota</div>
          </div>
          
          {/* 二维码 */}
          {qrCode && (
            <div style={{ 
              margin: '24px 0',
              padding: '20px',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              display: 'inline-block',
              width: '100%',
            }}>
              <Image 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCode)}`}
                size='medium'
                centered
                style={{ borderRadius: '8px' }}
              />
            </div>
          )}

          {/* 倒计时 */}
          <div style={{ 
            textAlign: 'center',
            marginTop: '20px',
            padding: '12px',
            background: countdown < 300 ? '#fff1f0' : '#e6f7ff',
            borderRadius: '8px',
            border: countdown < 300 ? '1px solid #ffccc7' : '1px solid #91d5ff',
          }}>
            <Icon name='clock outline' style={{ color: countdown < 300 ? '#ff4d4f' : '#1890ff', marginRight: '8px' }} />
            <span style={{ fontSize: '14px', color: countdown < 300 ? '#ff4d4f' : '#1890ff', fontWeight: '600' }}>
              订单有效期：{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
            </span>
          </div>
          
          {/* 支付成功提示 */}
          {paymentStatus === 'paid' && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              color: '#fff',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
            }}>
              <Icon name='check circle' style={{ fontSize: '32px', marginBottom: '8px' }} />
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>支付成功！</div>
              <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>余额已自动充值</div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{ textAlign: 'center', padding: '16px 24px' }}>
          <Button 
            onClick={() => {
              resetPaymentState();
            }}
            style={{
              borderRadius: '8px',
              padding: '12px 32px',
              fontSize: '15px',
            }}
          >
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default TopUp;
